import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Heading, Icon, Paragraph, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconAppDownloadFilled } from '@zillow/constellation-icons';
import { CONSTANTS } from '../constants';

export const NON_IA_TOP_BAR_BANNER_STYLE = {
  marginLeft: 'var(--ia-leftnav-width, 0px)',
} as const;

// Pins to --ia-topnav-bar-height (bar alone), not the total — the total includes this banner's own height.
export const IA_TOP_BAR_BANNER_STYLE = {
  position: 'fixed',
  top: 'var(--ia-topnav-bar-height, 0px)',
  left: 0,
  right: 0,
  zIndex: 100000,
} as const;

type BannerContent = {
  cookieName: string;
  cookieExp: number;
  headerText: string;
  bodyText: string;
  upsellHref: string;
};

/**
 * Returns the upsell href based on user login status and device type.
 *
 * @param {boolean} isLoggedIn - Whether the user is logged in.
 * @param {boolean} isIOS - Whether the user is on an iOS device.
 * @return {string} The upsell href.
 */
export function getUpsellHref(isLoggedIn: boolean, isIOS: boolean): string {
  return isLoggedIn
    ? isIOS
      ? CONSTANTS.HREF_IOS_LOGGEDIN
      : CONSTANTS.HREF_ANDROID_LOGGEDIN
    : isIOS
      ? CONSTANTS.HREF_IOS_LOGGEDOUT
      : CONSTANTS.HREF_ANDROID_LOGGEDOUT;
}

/**
 * Resolves whether the banner should render (path, device, and cookie checks) and, if so, the copy
 * and dismissal cookie to use. Returns null when the banner should be suppressed.
 */
function resolveBannerContent(): BannerContent | null {
  if (!window.location.pathname.startsWith('/rental-manager')) {
    return null;
  }

  const userAgent = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /android/i.test(userAgent);
  if (!isIos && !isAndroid) {
    return null;
  }

  const userCookie = Cookies.get(CONSTANTS.COOKIE_NAME_USER);
  const isLoggedIn = userCookie !== undefined && userCookie !== null;

  const cookieName = isLoggedIn ? CONSTANTS.COOKIE_NAME_LOGGEDIN : CONSTANTS.COOKIE_NAME_LOGGEDOUT;
  if (Cookies.get(cookieName) === CONSTANTS.COOKIE_VALUE) {
    return null;
  }

  // Suppress the banner if the user is on the app (`hostApp` cookie set to `ios` or `android`).
  const hostAppCookie = Cookies.get(CONSTANTS.COOKIE_NAME_HOSTAPP);
  if (hostAppCookie === CONSTANTS.COOKIE_VALUE_IOS || hostAppCookie === CONSTANTS.COOKIE_VALUE_ANDROID) {
    return null;
  }

  return {
    cookieName,
    cookieExp: isLoggedIn ? CONSTANTS.COOKIE_EXP_LOGGEDIN : CONSTANTS.COOKIE_EXP_LOGGEDOUT,
    headerText: isLoggedIn ? CONSTANTS.HEADER_TEXT_LOGGEDIN : CONSTANTS.HEADER_TEXT_LOGGEDOUT,
    bodyText: isLoggedIn ? CONSTANTS.BODY_TEXT_LOGGEDIN : CONSTANTS.BODY_TEXT_LOGGEDOUT,
    upsellHref: getUpsellHref(isLoggedIn, isIos),
  };
}

/**
 * A React functional component that renders an upsell banner for the Zillow Rental Manager app.
 * It checks the current path, device, and cookies to determine whether or not to display the banner.
 * If the banner is displayed, it includes a link to download the app for iOS or Android and a close button.
 *
 * @return {JSX.Element|null} The upsell banner component, or null if the banner should not be displayed.
 */
export const AppDownloadUpsellBanner = () => {
  const [browser, setBrowser] = useState<boolean>(false);
  const [isIaTopBar, setIsIaTopBar] = useState<boolean>(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBrowser(true);
  }, []);

  // TODO: needs looking at again after normalizing
  // Is the IA top bar shown? Read --ia-topnav-bar-height (published async by AppLayoutContainer, and
  // it flips at the xxl breakpoint), so re-read on <head> mutations (the injected <style>) and resize.
  useEffect(() => {
    if (!browser) {
      return;
    }
    const read = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--ia-topnav-bar-height');
      setIsIaTopBar((parseInt(raw, 10) || 0) > 0);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.head, { childList: true });
    window.addEventListener('resize', read);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [browser]);

  const content = browser ? resolveBannerContent() : null;
  const isPinnedBelowTopBar = content !== null && isIaTopBar;

  // While pinned below the IA top bar, publish the banner's height so AppLayoutContainer can push
  // the nav + content down to clear the fixed strip; clear it whenever it isn't pinned.
  useEffect(() => {
    const el = bannerRef.current;
    if (!isPinnedBelowTopBar || !el) {
      return;
    }
    const publish = () => {
      document.documentElement.style.setProperty('--app-banner-height', `${el.offsetHeight}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--app-banner-height');
    };
  }, [isPinnedBelowTopBar]);

  if (content === null) {
    return null;
  }

  return (
    <div ref={bannerRef} style={isIaTopBar ? IA_TOP_BAR_BANNER_STYLE : NON_IA_TOP_BAR_BANNER_STYLE}>
      <UpsellBanner
        tone="soft"
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            Cookies.set(content.cookieName, CONSTANTS.COOKIE_VALUE, { expires: content.cookieExp });
          }
        }}
        media={<Icon render={<IconAppDownloadFilled />} />}
        header={<Heading level={2}>{content.headerText}</Heading>}
        body={<Paragraph>{content.bodyText}</Paragraph>}
        action={
          <TextButton asChild>
            <a href={content.upsellHref}>Open app</a>
          </TextButton>
        }
      />
    </div>
  );
};
